import { NavLink, useLocation } from "react-router";
import { preloadRoute } from "./PreloadLink";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Wand2,
  Users,
  CalendarDays,
  Mail,
  Bookmark,
  History,
  CreditCard,
  Gift,
  ListChecks,
  Heart,
  Sparkles,
  Inbox,
  ShoppingBag,
  Ticket,
  Rss,
  Store,
  Headphones,
  FileText,
  Database,
  GitBranch,
  Bell,
  Layers,
  Share2,
  ServerCog,
  BarChart2,
  Images,
} from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface SidebarProps {
  collapsed?: boolean;
}

const mainNavItems = [
  {
    label: "Dashboard",
    icon: LayoutDashboard,
    path: "/dashboard",
  },
  {
    label: "Gift Wizard",
    icon: Wand2,
    path: "/wizard",
    highlight: true,
  },
  {
    label: "My People",
    icon: Users,
    path: "/contacts",
  },
  {
    label: "Calendar",
    icon: CalendarDays,
    path: "/calendar",
  },
  {
    label: "Cards",
    icon: Mail,
    path: "/cards",
  },
  {
    label: "My Cards",
    icon: Inbox,
    path: "/my-cards",
  },
];

const giftNavItems = [
  {
    label: "Browse Gifts",
    icon: ShoppingBag,
    path: "/browse",
  },
  {
    label: "Experiences",
    icon: Ticket,
    path: "/experiences",
  },
  {
    label: "Shop",
    icon: Store,
    path: "/shop",
  },
  {
    label: "Saved Gifts",
    icon: Bookmark,
    path: "/saved-gifts",
  },
  {
    label: "Gift History",
    icon: History,
    path: "/gift-history",
  },
  {
    label: "Gift Impact",
    icon: BarChart2,
    path: "/gift-impact",
  },
  {
    label: "Wish List",
    icon: ListChecks,
    path: "/wishlist",
  },
];

const socialNavItems = [
  {
    label: "Gift Exchange",
    icon: Gift,
    path: "/gift-exchange/create",
  },
  {
    label: "Group Gifts",
    icon: Heart,
    path: "/group-gift/create",
  },
];

const adminNavItems = [
  { label: "Dashboard", icon: LayoutDashboard, path: "/admin" },
  { label: "Users", icon: Users, path: "/admin/users" },
  { label: "Analytics", icon: BarChart2, path: "/admin/analytics" },
  { label: "Content", icon: Layers, path: "/admin/content" },
  { label: "Experience Deals", icon: Ticket, path: "/admin/experience-deals" },
  { label: "Affiliate Feeds", icon: Rss, path: "/admin/feeds" },
  { label: "Support", icon: Headphones, path: "/admin/support" },
  { label: "Email Templates", icon: FileText, path: "/admin/email-templates" },
  { label: "Email Preview", icon: Mail, path: "/admin/emails" },
  { label: "Notifications", icon: Bell, path: "/admin/notifications" },
  { label: "Audit Log", icon: History, path: "/admin/audit-log" },
  { label: "Migrations", icon: Database, path: "/admin/migrations" },
  { label: "Deployments", icon: GitBranch, path: "/admin/deployments" },
  { label: "Queues", icon: ServerCog, path: "/admin/queues" },
  { label: "Share Preview", icon: Share2, path: "/admin/share-preview" },
  { label: "OG Images", icon: Images, path: "/admin/og-preview" },
];

export function Sidebar({ collapsed = false }: SidebarProps) {
  const location = useLocation();
  const isAdminArea = location.pathname.startsWith("/admin");

  const renderNavItem = (
    item: {
      label: string;
      icon: React.ComponentType<{ className?: string }>;
      path: string;
      highlight?: boolean;
      exact?: boolean;
    }
  ) => {
    const Icon = item.icon;
    const isActive = item.exact
      ? location.pathname === item.path
      : location.pathname === item.path || location.pathname.startsWith(item.path + "/");

    return (
      <NavLink
        key={item.path}
        to={item.path}
        onMouseEnter={() => preloadRoute(item.path)}
        className={cn(
          "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
          item.highlight && !isActive && "text-gift-amber-400 hover:text-gift-amber-300",
          collapsed && "justify-center px-2"
        )}
      >
        <Icon
          className={cn(
            "h-5 w-5 shrink-0",
            item.highlight && !isActive && "text-gift-amber-400"
          )}
        />
        {!collapsed && <span>{item.label}</span>}
        {item.highlight && !collapsed && (
          <Sparkles className="h-3 w-3 ml-auto text-gift-amber-400" />
        )}
      </NavLink>
    );
  };

  return (
    <aside
      aria-label="Main navigation"
      className={cn(
        "hidden lg:flex flex-col border-r bg-card/50 backdrop-blur-sm transition-all duration-300",
        collapsed ? "w-16" : "w-64"
      )}
    >
      <ScrollArea className="flex-1 py-4">
        {isAdminArea ? (
          <nav aria-label="Admin" className="px-3 space-y-1">
            {!collapsed && (
              <p className="px-3 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                Admin
              </p>
            )}
            {adminNavItems.map((item) =>
              renderNavItem(item.path === "/admin" ? { ...item, exact: true } : item)
            )}
          </nav>
        ) : (
          <>
            <nav aria-label="Main" className="px-3 space-y-1">
              {!collapsed && (
                <p className="px-3 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Main
                </p>
              )}
              {mainNavItems.map(renderNavItem)}
            </nav>

            <div className="my-4 mx-3">
              <div className="h-px bg-border" />
            </div>

            <nav aria-label="Gift management" className="px-3 space-y-1">
              {!collapsed && (
                <p className="px-3 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Gifts
                </p>
              )}
              {giftNavItems.map(renderNavItem)}
            </nav>

            <div className="my-4 mx-3">
              <div className="h-px bg-border" />
            </div>

            <nav aria-label="Social features" className="px-3 space-y-1">
              {!collapsed && (
                <p className="px-3 pb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  Social
                </p>
              )}
              {socialNavItems.map(renderNavItem)}
            </nav>
          </>
        )}
      </ScrollArea>

      {!collapsed && !isAdminArea && (
        <div className="p-4 border-t">
          <NavLink
            to="/subscription"
            className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium bg-gift-gradient text-white hover:opacity-90 transition-opacity"
          >
            <CreditCard className="h-5 w-5 shrink-0" />
            <span>Upgrade Plan</span>
          </NavLink>
        </div>
      )}
    </aside>
  );
}
